/**
 * HelpDropdown - Hover dropdown menu for help options
 * 
 * Shows 3 options on hover:
 * - Help (opens HelpModal)
 * - Learning Center (opens LearningCenterModal)
 * - Contact Us & Support (opens ContactModal)
 */

import React, { useState, useRef, useEffect } from 'react';

const HelpDropdown = ({ onHelpClick, onLearningClick, onContactClick, onFAQClick }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const timeoutRef = useRef(null);

  // Handle mouse enter - open dropdown
  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsOpen(true);
  };

  // Handle mouse leave - close with delay
  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const menuItems = [
    {
      id: 'help',
      label: 'Help',
      icon: '📖',
      description: 'User guide & documentation',
      onClick: onHelpClick
    },
    {
      id: 'faq',
      label: 'FAQs',
      icon: '❓',
      description: 'Frequently asked questions',
      onClick: onFAQClick
    },
    {
      id: 'learning',
      label: 'Learning Center',
      icon: '🎓',
      description: 'Tutorials & training',
      onClick: onLearningClick
    },
    {
      id: 'contact',
      label: 'Contact Us & Support',
      icon: '🎧',
      description: 'Get help from our team',
      onClick: onContactClick
    }
  ];

  const handleItemClick = (item) => {
    setIsOpen(false);
    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <div
      ref={dropdownRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-tutorial="help-dropdown"
      style={{ position: 'relative', display: 'inline-block' }}
    >
      {/* Trigger Button - matches header button style, 44x44px square */}
      <button
        style={{
          width: '44px',
          height: '44px',
          padding: 0,
          background: 'transparent',
          borderRadius: '8px',
          cursor: 'pointer',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          transition: 'all 0.2s ease',
          fontSize: '1rem',
          fontWeight: '600',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'linear-gradient(135deg, rgba(220, 53, 69, 0.95) 0%, rgba(185, 28, 28, 0.95) 100%)';
          e.currentTarget.style.borderColor = '#dc3545';
          e.currentTarget.style.transform = 'translateY(-1px)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(220, 53, 69, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          e.currentTarget.style.transform = 'translateY(0)';
          e.currentTarget.style.boxShadow = 'none';
        }}
        title="Help & Support"
      >
        ?
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
            minWidth: '260px',
            overflow: 'hidden',
            zIndex: 10000,
            animation: 'dropdownFadeIn 0.2s ease',
          }}
        >
          {/* Header */}
          <div style={{
            padding: '14px 16px',
            background: 'linear-gradient(135deg, #1a1a1a 0%, #dc3545 100%)',
            color: 'white',
          }}>
            <div style={{ fontWeight: '600', fontSize: '0.95rem' }}>
              Help & Resources
            </div>
          </div>

          {/* Menu Items */}
          <div style={{ padding: '8px' }}>
            {menuItems.map((item, index) => (
              <button
                key={item.id}
                onClick={() => handleItemClick(item)}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: 'transparent',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  textAlign: 'left',
                  transition: 'background-color 0.15s ease',
                  marginBottom: index < menuItems.length - 1 ? '4px' : 0,
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f3f4f6';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <span style={{
                  fontSize: '1.5rem',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                }}>
                  {item.icon}
                </span>
                <div>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    color: '#111827',
                    marginBottom: '2px',
                  }}>
                    {item.label}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#6b7280',
                  }}>
                    {item.description}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes dropdownFadeIn {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default HelpDropdown;
